import { useEffect, useMemo, useRef } from "react"
import { useQuery } from "@rocicorp/zero/react"
import { Link } from "@tanstack/react-router"
import { useVirtualizer } from "@tanstack/react-virtual"
import { IconSquareChevronUpOutline18 as ChevronUpSquareIcon } from "nucleo-ui-outline-18"

import { formatRelativeTime } from "@productlane/lib/src/date"
import { Avatar } from "@productlane/ui"
import { queries } from "@productlane/zero/queries"

import { areQueriesSettled } from "@/lib/query-settled"
import { scrollParent } from "@/lib/scroll-parent"

const ITEM_SIZE = 52 // Approximate height of a voter row incl. spacing

interface Voter {
	id: string
	contactId: string
	contactName: string | null
	contactEmail: string
	contactImageUrl: string | null
	itemLabel?: string
	createdAt: string | number | Date
}

interface DetailUpvotesListProps {
	contactId?: string
	companyId?: string
	projectId?: string
	issueId?: string
	hideLabel?: boolean
	setThreadsSize?: (size: number, isSettled: boolean) => void
}

export const DetailUpvotesList = ({
	contactId,
	companyId,
	projectId,
	issueId,
	setThreadsSize,
	hideLabel = false,
}: DetailUpvotesListProps) => {
	const parentRef = useRef<HTMLDivElement>(null)

	const [projectUpvotes, projectUpvotesResult] = useQuery(
		queries.projectUpvoters({ projectId, contactId, companyId }),
	)
	const [issueUpvotes, issueUpvotesResult] = useQuery(
		queries.issueUpvoters({ issueId, contactId, companyId }),
	)
	const isSettled = areQueriesSettled(projectUpvotesResult, issueUpvotesResult)

	// In contact/company context, each voter upvoted a different item, so we
	// surface which project or issue the vote was for.
	const showItem = Boolean(companyId || contactId)

	const voters = useMemo<Voter[]>(() => {
		const entries: Voter[] = [
			...projectUpvotes
				.filter((u) => u.contact)
				.map((u) => ({
					id: u.id,
					contactId: u.contactId,
					contactName: u.contact?.name ?? null,
					contactEmail: u.contact?.email ?? "",
					contactImageUrl: u.contact?.imageUrl ?? null,
					itemLabel: u.project?.name ?? undefined,
					createdAt: u.createdAt,
				})),
			...issueUpvotes
				.filter((u) => u.contact)
				.map((u) => ({
					id: u.id,
					contactId: u.contactId,
					contactName: u.contact?.name ?? null,
					contactEmail: u.contact?.email ?? "",
					contactImageUrl: u.contact?.imageUrl ?? null,
					itemLabel: u.issue?.title ?? undefined,
					createdAt: u.createdAt,
				})),
		]
		return entries.sort(
			(a, b) =>
				new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
		)
	}, [projectUpvotes, issueUpvotes])

	const virtualizer = useVirtualizer({
		count: voters.length,
		getScrollElement: () =>
			parentRef.current ? scrollParent(parentRef.current) : null,
		estimateSize: () => ITEM_SIZE,
		overscan: 5,
	})

	const virtualItems = virtualizer.getVirtualItems()

	useEffect(() => {
		if (setThreadsSize) {
			setThreadsSize(voters.length, isSettled)
		}
	}, [voters, isSettled, setThreadsSize])

	return (
		<div ref={parentRef}>
			{voters.length > 0 && (
				<div className="mt-4 pb-12">
					{!hideLabel && (
						<div className="px-5 text-lg font-semibold sm:px-8">Upvotes</div>
					)}

					<div
						className="relative mt-4"
						style={{ height: `${virtualizer.getTotalSize()}px` }}
					>
						{virtualItems.map((virtualItem) => {
							const voter = voters[virtualItem.index]
							if (!voter) return null

							return (
								<div
									key={virtualItem.key}
									className="absolute left-0 top-0 w-full px-3"
									style={{
										height: `${virtualItem.size}px`,
										transform: `translateY(${virtualItem.start}px)`,
									}}
								>
									<VoterRow voter={voter} showItem={showItem} />
								</div>
							)
						})}
					</div>
				</div>
			)}
		</div>
	)
}

const VoterRow = ({ voter, showItem }: { voter: Voter; showItem: boolean }) => {
	const displayName = voter.contactName || voter.contactEmail

	return (
		<Link
			to="/people/$id"
			params={{ id: voter.contactId }}
			className="flex select-none items-center gap-2 rounded-md py-2 text-sm hover:bg-gray-100 md:px-4 dark:hover:bg-gray-850"
		>
			<Avatar
				name={displayName}
				image={voter.contactImageUrl ?? undefined}
				size="sm"
			/>
			<div className="min-w-0 flex-1 truncate font-medium">{displayName}</div>
			{showItem && voter.itemLabel ? (
				<div className="flex flex-row items-center gap-1 truncate">
					<div className="text-tertiary">⋅</div>
					<div className="truncate text-tertiary">{voter.itemLabel}</div>
				</div>
			) : null}
			<div className="flex-none text-secondary">
				{formatRelativeTime(new Date(voter.createdAt), true)}
			</div>
			<ChevronUpSquareIcon className="ml-2 h-3 w-3 flex-none" />
		</Link>
	)
}
